import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, delay, map, switchMap } from 'rxjs/operators';
import {
    addLabRequestDocumentTypeConfiguration,
    addLabRequestDocumentTypeConfigurationFailure,
    addLabRequestDocumentTypeConfigurationSuccess, getCandidateVendorTestCodes, getCandidateVendorTestCodesFailure,
    getCandidateVendorTestCodesSuccess, getDsDocumentTypeIds,
    getDsDocumentTypeIdsFailure,
    getDsDocumentTypeIdsSuccess,
    getDsHasDocumentTypeConfiguration,
    getDsHasDocumentTypeConfigurationFailure,
    getDsHasDocumentTypeConfigurationSuccess,
    getLabRequestDocumentTypeConfiguration,
    getLabRequestDocumentTypeConfigurationFailure,
    getLabRequestDocumentTypeConfigurationSuccess,
    getLabVendors,
    getLabVendorsFailure,
    getLabVendorsSuccess,
    updateLabRequestDocumentTypeConfiguration,
    updateLabRequestDocumentTypeConfigurationFailure,
    updateLabRequestDocumentTypeConfigurationSuccess
} from 'src/app/compliance/store/actions/compliance-lab.actions';
import { showToasterMessage } from 'src/app/shared/actions/toaster.actions';
import { Lookup } from '../../../shared/models/lookup.model';
import { ComplianceRequestsClient } from '../../api/compliance-requests-client.service';
import { ComplianceLabRequestDocumentTypeConfigurationDto } from '../../api/models/compliance-lab-request-document-type-configuration-dto.model';

@Injectable()
export class ComplianceLabEffects {
    constructor(
        private readonly _actions$: Actions,
        private readonly _store: Store,
        private readonly complianceRequestsClient: ComplianceRequestsClient
    ) {}

    getLabVendors$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(getLabVendors),
            switchMap(() => {
                return this.complianceRequestsClient.getLabRequestVendors().pipe(
                    map((result: Lookup[]) => getLabVendorsSuccess({ result: result })),
                    catchError((result) => of(getLabVendorsFailure({ error: result })))
                );
            })
        );
    });

    getDsHasDocumentTypeConfiguration$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(getDsHasDocumentTypeConfiguration),
            delay(5000),
            switchMap((action) => {
                return this.complianceRequestsClient
                    .getDsHasConfiguration(action.documentTypeLevel, action.entityId)
                    .pipe(
                        map((result) => getDsHasDocumentTypeConfigurationSuccess({ result: result })),
                        catchError((result) => of(getDsHasDocumentTypeConfigurationFailure({ error: result })))
                    );
            })
        );
    });

    getDsDocumentTypeIds$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(getDsDocumentTypeIds),
            switchMap(() => {
                return this.complianceRequestsClient.getDsDocumentTypeIds().pipe(
                    map((result) => getDsDocumentTypeIdsSuccess({ result: result })),
                    catchError((result) => of(getDsDocumentTypeIdsFailure({ error: result })))
                );
            })
        );
    });

    getLabRequestDocumentTypeConfiguration$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(getLabRequestDocumentTypeConfiguration),
            switchMap((action) => {
                return this.complianceRequestsClient
                    .getLabRequestDocumentTypeConfiguration(
                        action.documentTypeId,
                        action.documentTypeLevelType,
                        action.levelId
                    )
                    .pipe(
                        map((result: ComplianceLabRequestDocumentTypeConfigurationDto) => getLabRequestDocumentTypeConfigurationSuccess({ result: result })),
                        catchError((result) => of(getLabRequestDocumentTypeConfigurationFailure({ error: result })))
                    );
            })
        );
    });

    addLabRequestDocumentTypeConfiguration$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(addLabRequestDocumentTypeConfiguration),
            switchMap((action) => {
                return this.complianceRequestsClient.addLabRequestDocumentTypeConfiguration(action.configuration).pipe(
                    map(() =>
                        getLabRequestDocumentTypeConfiguration({
                            documentTypeId: action.configuration.documentTypeId,
                            documentTypeLevelType: action.configuration.facilityId
                                ? 2
                                : action.configuration.qmAssociationId
                                  ? 1
                                  : 3,
                            levelId:
                                action.configuration.facilityId ??
                                action.configuration.qmAssociationId ??
                                action.configuration.stateId
                        })
                    ),
                    map(() =>
                        this._store.dispatch(
                            showToasterMessage({ message: `Document Type Configuration has been created.` })
                        )
                    ),
                    map(() => addLabRequestDocumentTypeConfigurationSuccess()),
                    catchError((result) => {
                        this._store.dispatch(
                            showToasterMessage({
                                message: `An error occurred when creating Document Type Configuration.`
                            })
                        );
                        return of(addLabRequestDocumentTypeConfigurationFailure({ error: result }));
                    })
                );
            })
        );
    });

    updateLabRequestDocumentTypeConfiguration$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(updateLabRequestDocumentTypeConfiguration),
            switchMap((action) => {
                return this.complianceRequestsClient
                    .updateLabRequestDocumentTypeConfiguration(action.documentTypeId, action.configuration)
                    .pipe(
                        map(() =>
                            getLabRequestDocumentTypeConfiguration({
                                documentTypeId: action.configuration.documentTypeId,
                                documentTypeLevelType: action.configuration.facilityId
                                    ? 2
                                    : action.configuration.qmAssociationId
                                      ? 1
                                      : 3,
                                levelId:
                                    action.configuration.facilityId ??
                                    action.configuration.qmAssociationId ??
                                    action.configuration.stateId
                            })
                        ),
                        map(() =>
                            this._store.dispatch(
                                showToasterMessage({ message: `Document Type Configuration has been updated.` })
                            )
                        ),
                        map(() => updateLabRequestDocumentTypeConfigurationSuccess()),
                        catchError((result) => {
                            this._store.dispatch(
                                showToasterMessage({
                                    message: `An error occurred when updating Document Type Configuration.`
                                })
                            );
                            return of(updateLabRequestDocumentTypeConfigurationFailure({ error: result }));
                        })
                    );
            })
        );
    });

    getCandidateVendorTestCodes$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(getCandidateVendorTestCodes),
            switchMap((action) => {
                return this.complianceRequestsClient.getCandidateVendorTestCodes(action.candidateId).pipe(
                    map((result) => getCandidateVendorTestCodesSuccess({ testCodes: result })),
                    catchError((result) => of(getCandidateVendorTestCodesFailure({ error: result })))
                );
            })
        );
    });
}
